import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { api, formatINR } from "@/lib/api";

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [amount, setAmount] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="pt-[72px] min-h-screen flex items-center justify-center">
      <div className="max-w-xl mx-auto px-5 text-center py-20" data-testid="payment-result">
        {state === "checking" && (
          <>
            <div className="w-14 h-14 border-2 border-line border-t-crimson rounded-full animate-spin mx-auto" />
            <p className="overline mt-8">Confirming your payment…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={72} weight="thin" className="text-gold mx-auto" />
            <h1 className="font-serif text-6xl md:text-7xl text-bone mt-8 leading-none">Thank you.</h1>
            <p className="text-ash mt-6 leading-relaxed">
              Your order {amount ? `of ${formatINR(amount)} ` : ""}is confirmed. The atelier will begin your piece shortly —
              a confirmation will follow. Welcome to TAMASHii.
            </p>
            <Link to="/shop" className="btn-fill inline-flex items-center gap-3 px-10 py-4 overline text-sm mt-10">
              Continue Shopping <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={72} weight="thin" className="text-crimson mx-auto" />
            <h1 className="font-serif text-5xl text-bone mt-8">Payment not confirmed</h1>
            <p className="text-ash mt-6">We could not confirm your payment. If you were charged, it will be reflected shortly. Please try again.</p>
            <Link to="/cart" className="btn-fill inline-flex items-center gap-3 px-10 py-4 overline text-sm mt-10">
              Return to Bag <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
